import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Collapse,
  Grid,
  Chip,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  SvgIcon,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import AppleIcon from '@mui/icons-material/Apple';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { supabase } from './supabaseClient';
import ProfileDialog from './ProfileDialog';

import { prettyStatus } from './labels';

// Google's own four-colour mark. @mui/icons-material/Google is a single path,
// so it can only ever be one colour — the four quadrants have to be four paths
// with their own fills. Defined outside the component so it is not rebuilt on
// every render, and sized by the caller like any other icon.
const GoogleColorIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </SvgIcon>
);
// ProfilesPage - Collapsible Row Design v3.0 - NO TABLES!
// Updated: Removed ALL table code, using ONLY collapsible rows
// Version: 2024-12-08-v3-FINAL
// CACHE BUST: If you see "Columns to set" in console, browser is using OLD cached version!
export default function ProfilesPage() {
  console.log('🔵 ProfilesPage v3.0-FINAL: COLLAPSIBLE DESIGN - NO TABLES!');
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogPhone, setDialogPhone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [hideEmpty, setHideEmpty] = useState(true);

  const buildPublicUrl = useCallback((bucket, path) => {
    if (!path) return null;
    const trimmed = path.replace(/^https?:\/\/[^/]+\/storage\/v1\/object\/public\//, '');
    if (trimmed.startsWith('http')) return trimmed;
    const clean = trimmed.replace(/^\/+/, '');
    const base = process.env.REACT_APP_SUPABASE_URL;
    return `${base}/storage/v1/object/public/${bucket}/${clean}`;
  }, []);

  const normalizeProfileImage = useCallback((img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    const clean = img.startsWith('profiles/') ? img : `profiles/${img}`;
    return buildPublicUrl('profile-images', clean);
  }, [buildPublicUrl]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'success' });
    
    try {
      console.log('ProfilesPage: Attempting to fetch profiles...');
      const { data, error } = await supabase.from('Profiles').select('*');
      
      if (error) {
        console.error('ProfilesPage: Error fetching Profiles:', error);
        const isRLSError = error.message?.includes('policy') || 
                          error.message?.includes('permission') ||
          error.code === '42501' ||
          error.code === 'PGRST301';
        
        if (isRLSError) {
          setSnackbar({ 
            open: true, 
            message: `RLS Policy Error: ${error.message}. Please run FIX_PROFILES_ACCESS_NOW.sql in Supabase SQL Editor.`, 
            severity: 'error' 
          });
        } else {
          setSnackbar({ open: true, message: `Load failed: ${error.message}`, severity: 'error' });
        }
        
        setRows([]);
        setAllRows([]);
        setLoading(false);
        return;
      }

      console.log('🔵 ProfilesPage v3.0: Profiles fetched:', data?.length || 0, 'profiles');
      console.log('🔵 ProfilesPage v3.0: Using COLLAPSIBLE ROW DESIGN - NO TABLES!');
      console.log('🔵 If you see "Columns to set" above, you are running OLD cached code!');

      if (!data || data.length === 0) {
        setRows([]);
        setAllRows([]);
        setLoading(false);
        setSnackbar({ 
          open: true, 
          message: 'No profiles found. Check RLS policies or database connection.', 
          severity: 'warning' 
        });
        return;
      }

      // Enrich image URLs
      const enriched = data.map(row => {
        const imageUrl = normalizeProfileImage(row.profile_image) || row.profile_image_url || null;
        let additionalImages = [];
        if (row.additional_images) {
          try {
            const arr = Array.isArray(row.additional_images) ? row.additional_images : JSON.parse(row.additional_images);
            additionalImages = arr.filter(Boolean).map(img => {
              if (!img) return null;
              if (img.startsWith('http')) return img;
              const clean = img.replace(/^additional_images\//, '').replace(/^\/+/, '');
              return buildPublicUrl('additional_images', clean);
            }).filter(Boolean);
          } catch (_) { }
        }
        return { ...row, imageUrl, additionalImages };
      });
      
      console.log('ProfilesPage: Setting rows with collapsible design');
      setRows(enriched);
      setAllRows(enriched);
    } catch (err) {
      console.error('Unexpected error fetching Profiles:', err);
      setSnackbar({ open: true, message: `Unexpected error: ${err.message}`, severity: 'error' });
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildPublicUrl, normalizeProfileImage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live-update the list when a profile is created/edited elsewhere instead
  // of requiring a manual refresh.
  useEffect(() => {
    const channel = supabase
      .channel('profiles-page-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'Profiles' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchData]);

  // Filter and search
  useEffect(() => {
    let filtered = [...allRows];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(row => {
        const name = `${row.first_name || ''} ${row.last_name || ''}`.toLowerCase();
        const email = (row.email || '').toLowerCase();
        const phone = (row.phone_number || '').toLowerCase();
        return name.includes(term) || email.includes(term) || phone.includes(term);
      });
    }

    if (userTypeFilter !== 'all') {
      filtered = filtered.filter(row => row.user_type === userTypeFilter);
    }

    setRows(filtered);
  }, [searchTerm, userTypeFilter, allRows]);

  const toggleRow = (rowId) => {
    const rowIdString = String(rowId);
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIdString)) {
      newExpanded.delete(rowIdString);
    } else {
      newExpanded.add(rowIdString);
    }
    setExpandedRows(newExpanded);
  };

  const openProfile = (phone) => setDialogPhone(phone);
  const closeProfile = () => setDialogPhone(null);

  const clearFilters = () => {
    setSearchTerm('');
    setUserTypeFilter('all');
  };

  const hasActiveFilters = searchTerm || userTypeFilter !== 'all';

  const getUserTypeColor = (userType) => {
    switch (userType?.toLowerCase()) {
      case 'stylist':
        return 'primary';
      case 'client':
        return 'success';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // How this account signs in. One account has one way in, enforced by
  // guard_one_login_method, so this is a single answer and not a list.
  //
  // The fallback matches login_method_for_phone in the database: a row whose
  // auth_provider was never set is read from the identity it carries, so a
  // Google account never shows here as a phone account.
  const getSignInMethod = (row) => {
    const pinned = (row.auth_provider || '').trim().toLowerCase();
    if (pinned === 'apple' || pinned === 'google' || pinned === 'phone') return pinned;
    if ((row.apple_user_id || '').trim()) return 'apple';
    if ((row.google_user_id || '').trim()) return 'google';
    return 'phone';
  };

  const SIGN_IN_METHODS = {
    apple: { label: 'Apple', Icon: AppleIcon, iconColor: 'text.primary' },
    google: { label: 'Google', Icon: GoogleColorIcon, iconColor: undefined },
    phone: { label: 'Phone', Icon: PhoneIphoneIcon, iconColor: 'text.secondary' },
  };

  const formatFieldName = (field) => {
    return field.replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Fields to show in summary row
  const summaryFields = ['imageUrl', 'first_name', 'last_name', 'email', 'phone_number', 'user_type'];

  // Get all other fields for details section
  const getDetailFields = (row) => {
    return Object.keys(row).filter(field => !summaryFields.includes(field) && field !== 'additionalImages');
  };

  // Credentials, not profile data. A push token is enough to send a
  // notification to someone's device, so the row says whether one is on file
  // and not what it is. pin_hash and the Plaid columns are empty today — PINs
  // live in profile_pins — but they are listed here so they stay masked if
  // anything ever writes to them.
  const SENSITIVE_FIELDS = new Set([
    'pin_hash',
    'plaid_access_token_encrypted',
    'plaid_item_id',
    'device_token',
    'apns_token',
    'fcm_token',
  ]);

  // 81 columns in one alphabetical run is unreadable, so they are grouped by
  // what someone opening a profile is actually trying to answer. Any column
  // missing from this list still renders, under "Other" — a new column must
  // never silently disappear from the console.
  const FIELD_GROUPS = [
    ['Sign-in & identity', ['auth_provider', 'apple_user_id', 'google_user_id', 'email_verified_at',
      'has_pin', 'identity_verified', 'identity_verified_at', 'date_of_birth',
      'legal_name', 'legal_name_verified_at']],
    ['Profile', ['display_name', 'about_me', 'profile_image', 'gender', 'preferred_location',
      'signature_style', 'intro_video_url', 'show_online_status', 'last_seen_at']],
    ['Style & sizing', ['preferences', 'brands', 'favorite_brands', 'top_looks', 'size',
      'shirt_size', 'pant_size', 'dress_size', 'height', 'body_type']],
    ['Stylist work', ['expertise', 'rate', 'years_of_experiences', 'service_radius_miles',
      'cancellation_policy', 'no_show_fee', 'session_types', 'average_rating',
      'job_success_rate', 'completed_jobs', 'total_hours', 'total_earnings',
      'badges', 'media']],
    ['Links', ['personal_website', 'instagram', 'tiktok', 'pinterest']],
    ['Approval & compliance', ['stylist_status', 'is_published', 'stylist_status_reason',
      'stylist_status_updated_at', 'stylist_status_updated_by', 'admin_notes',
      'background_check_cleared', 'background_check_cleared_at', 'background_check_expires_at',
      'insurance_carrier', 'insurance_certificate_path', 'insurance_expires_on',
      'insurance_verified_at', 'insurance_verified_by']],
    ['Agreements & consent', ['terms_accepted_at', 'terms_version', 'privacy_accepted_at',
      'privacy_version', 'ai_photo_consent', 'ai_photo_consent_at', 'ai_photo_consent_version']],
    ['Credentials on file', ['pin_hash', 'plaid_access_token_encrypted', 'plaid_item_id',
      'device_token', 'apns_token', 'fcm_token']],
    ['System', ['id', 'created_at']],
  ];

  const isEmptyValue = (value) =>
    value === null || value === undefined || value === '' ||
    (Array.isArray(value) && value.length === 0);

  // Groups each row's fields, keeping anything this page has not been told
  // about under "Other".
  const getGroupedFields = (row) => {
    const available = new Set(getDetailFields(row));
    const groups = [];
    const placed = new Set();

    FIELD_GROUPS.forEach(([title, fields]) => {
      const present = fields.filter((f) => available.has(f));
      present.forEach((f) => placed.add(f));
      let visible = present;
      if (hideEmpty) {
        visible = present.filter((f) => SENSITIVE_FIELDS.has(f) || !isEmptyValue(row[f]));
      }
      if (visible.length > 0) groups.push([title, visible]);
    });

    const leftover = [...available].filter((f) => !placed.has(f));
    const visibleLeftover = hideEmpty ? leftover.filter((f) => !isEmptyValue(row[f])) : leftover;
    if (visibleLeftover.length > 0) groups.push(['Other', visibleLeftover.sort()]);

    return groups;
  };

  const countHiddenEmpty = (row) =>
    getDetailFields(row).filter((f) => !SENSITIVE_FIELDS.has(f) && isEmptyValue(row[f])).length;

  // Debug: Log when component renders
  useEffect(() => {
    console.log('ProfilesPage: Component rendered with collapsible design');
    console.log('ProfilesPage: Rows count:', rows.length);
    console.log('ProfilesPage: Expanded rows:', Array.from(expandedRows));
  }, [rows.length, expandedRows]);

  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Profiles ({rows.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage profiles (Collapsible View)
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Chip label="Live" size="small" color="success" variant="outlined" sx={{ height: 24, fontSize: '0.7rem', mr: 1 }} />
        <IconButton onClick={fetchData}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search profiles..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>User Type</InputLabel>
            <Select
              value={userTypeFilter}
              label="User Type"
              onChange={(e) => setUserTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Stylist">Stylist</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Profiles Found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {allRows.length === 0 
              ? 'There are no profiles in the database. This could be due to RLS policies blocking access or the database being empty.' 
              : 'No profiles match your current filters.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button 
              onClick={fetchData} 
              variant="contained" 
              size="small"
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="outlined" size="small">
                Clear Filters
              </Button>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          {rows.map((row, idx) => {
            const rowId = String(row.id || idx);
            const isExpanded = expandedRows.has(rowId);
            const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A';

                        return (
              <Box key={rowId}>
                {/* Summary Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => toggleRow(rowId)}
                >
                  {/* Profile Image */}
                  <Box sx={{ mr: 2, flexShrink: 0 }}>
                    {row.imageUrl ? (
                      <Avatar
                        src={row.imageUrl}
                        alt={fullName}
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
                        {fullName !== 'N/A' ? fullName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    )}
                  </Box>

                  {/* User Info */}
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, flexShrink: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {fullName}
                      </Typography>
                      {(row.identity_verified === true || row.identity_verified === 'true' || row.identity_verified === 1) && (
                        <VerifiedIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, flexShrink: 0 }}>
                      {row.email || 'N/A'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 150, flexShrink: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.phone_number) openProfile(row.phone_number);
                      }}
                    >
                      {row.phone_number || 'N/A'}
                    </Typography>
                    <Chip
                      label={prettyStatus(row.user_type) || 'N/A'}
                      color={getUserTypeColor(row.user_type)}
                      size="small"
                      sx={{ minWidth: 80, flexShrink: 0 }}
                    />
                  </Box>

                  {/* How this account signs in — kept at the end of the row,
                      away from the status chips, so it reads as a property of
                      the person rather than another badge. */}
                  {(() => {
                    const method = getSignInMethod(row);
                    const { label, Icon, iconColor } = SIGN_IN_METHODS[method];
                    return (
                      <Box
                        title={`Signs in with ${label}`}
                        sx={{ width: 24, mr: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Icon sx={{ fontSize: 20, color: iconColor }} />
                      </Box>
                    );
                  })()}

                  {/* Expand/Collapse Icon */}
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <Collapse in={isExpanded}>
                  <Box sx={{ p: 3, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Full Details
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={hideEmpty}
                            onChange={(e) => setHideEmpty(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {`Hide empty fields${hideEmpty ? ` (${countHiddenEmpty(row)} hidden)` : ''}`}
                          </Typography>
                        }
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Additional Images Section */}
                    {row.additionalImages && row.additionalImages.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                          Additional Images
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {row.additionalImages.map((img, idx) => (
                            <Box
                              key={idx}
                              component="img"
                              src={img}
                              alt={`Additional image ${idx + 1}`}
                              sx={{
                                width: 100,
                                height: 100,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.05)',
                                  transition: 'all 0.2s'
                                }
                              }}
                            />
                          ))}
                        </Box>
                        <Divider sx={{ mt: 2, mb: 2 }} />
                      </Box>
                    )}
                    
                    {getGroupedFields(row).map(([groupTitle, fields]) => (
                      <Box key={groupTitle} sx={{ mb: 2.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}
                        >
                          {groupTitle}
                        </Typography>
                        <Grid container spacing={2}>
                          {fields.map((field) => {
                            const value = row[field];
                            const isIdentityVerified = field === 'identity_verified' && (value === true || value === 'true' || value === 1);
                            const isSensitive = SENSITIVE_FIELDS.has(field);
                            const isSignInMethod = field === 'auth_provider';
                            const method = isSignInMethod ? getSignInMethod(row) : null;
                            const MethodIcon = method ? SIGN_IN_METHODS[method].Icon : null;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={field}>
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}
                                  >
                                    {isSignInMethod ? 'Signs In With' : formatFieldName(field)}:
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isSignInMethod ? (
                                      <>
                                        <MethodIcon fontSize="small" />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {SIGN_IN_METHODS[method].label}
                                        </Typography>
                                      </>
                                    ) : isSensitive ? (
                                      <Typography
                                        variant="body2"
                                        color={isEmptyValue(value) ? 'text.secondary' : 'warning.main'}
                                      >
                                        {isEmptyValue(value) ? 'None on file' : 'On file (hidden)'}
                                      </Typography>
                                    ) : isIdentityVerified ? (
                                      <>
                                        <VerifiedIcon color="primary" fontSize="small" />
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                          Yes
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {formatFieldValue(value)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
                            </Box>
                      );
                    })}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>

      <ProfileDialog open={Boolean(dialogPhone)} phoneNumber={dialogPhone} onClose={closeProfile} />
    </Box>
  );
}
